import { Divider, Drawer, GradeRangeSelector } from '@/components';
import { FilterOption, OptionType } from '@/components/FlyerboardFilters/FilterOption';

import { BodyLargeEmphasized, ButtonFlatMedium, ButtonPrimaryMedium } from '@/design-system';
import colors from '@/theme/colors';
import { css } from '@emotion/css';
import { PropsWithChildren } from 'react';

type JarFilterDrawerProps = {
  isOpen: boolean;
  onToggle: (state: boolean) => void;
  filters: {
    cost: string[];
    provider: string[];
    types: string[];
  };
  setFilters: (filters: any) => void;
  gradeFilter: { from?: string; to?: string };
  setGradeFilter: (gradeFilter: any) => void;
  loading: boolean;
  viewResultsLabel: string;
  resetFilters: () => void;
  viewResults: () => void;
  handleToggleOption: (section: string, value: string) => void;
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  generateProviderOptions: () => OptionType[];
  generateCostOption: () => OptionType[];
  generateTypesOptions: () => OptionType[];
  disableClearAllButton: boolean;
  grades: any[];
  lowGrade: string;
  highGrade: string;
  setDefaultGrades: () => void;
};

export const JarFilterDrawer: React.FC<PropsWithChildren<JarFilterDrawerProps>> = props => {
  const {
    isOpen,
    onToggle,
    filters,
    gradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
  } = props;

  const sections = [
    {
      name: 'provider',
      options: generateProviderOptions(),
    },
    {
      name: 'cost',
      options: generateCostOption(),
    },
    {
      name: 'types',
      options: generateTypesOptions(),
    },
  ];

  const renderSection = ({
    name,
    label,
    content,
    isLast,
  }: {
    name: string;
    label: string;
    content: React.ReactNode;
    isLast: boolean;
  }) => (
    <div
      key={name}
      className={styles.content}
      aria-labelledby={`filter-${name}-label`}
      role="group"
    >
      <BodyLargeEmphasized className={styles.filterName} id={`filter-${name}-label`}>
        {label}
      </BodyLargeEmphasized>
      <div className={styles.optionsContainer}>{content}</div>
      {!isLast && (
        <div className={styles.divider}>
          <Divider />
        </div>
      )}
    </div>
  );

  const FilterList = () => {
    const extraSectionAfter = 'cost';

    const items: {
      name: string;
      label: string;
      content: React.ReactNode;
    }[] = sections.flatMap(({ name, options }) => {
      const base = {
        name,
        label: name,
        content: options.map(({ value, description, disabled = false }: OptionType) => {
          const isChecked = (filters as any)[name].includes(value);
          return (
            <FilterOption
              key={value}
              description={description}
              value={value}
              filterName={name}
              checked={isChecked}
              handleToggle={() => () => handleToggleOption(name, value)}
              disabled={disabled && !isChecked}
            />
          );
        }),
      };

      if (name === extraSectionAfter) {
        return [
          base,
          {
            name: 'grade',
            label: 'Grade',
            content: (
              <GradeRangeSelector
                fromGrade={gradeFilter.from}
                toGrade={gradeFilter.to}
                onChange={handleGradeChange}
                grades={grades}
              />
            ),
          },
        ];
      }

      return [base];
    });

    return (
      <div className={styles.filterList}>
        {items.map((item, index) =>
          renderSection({
            ...item,
            isLast: index === items.length - 1,
          })
        )}
      </div>
    );
  };

  return (
    <>
      <Drawer title="Filters" isOpen={isOpen} onToggle={() => onToggle(false)} full>
        <div className={styles.container}>
          <FilterList />
          <div className={styles.footer}>
            <ButtonFlatMedium
              data-testid="clearAllButton"
              onClick={resetFilters}
              disabled={disableClearAllButton}
            >
              Reset Filters
            </ButtonFlatMedium>
            <ButtonPrimaryMedium
              onClick={viewResults}
              data-testid="showResultsButton"
              loading={loading}
            >
              {viewResultsLabel}
            </ButtonPrimaryMedium>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const styles = {
  filterList: css`
    flex-grow: 1;
  `,
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  `,
  footer: css`
    box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    position: sticky !important;
    background-color: ${colors.white};
    z-index: 1199;
    bottom: 0px;
    padding: 24px !important;
    margin-top: 25px;
    display: flex;
    justify-content: space-between !important;
    width: 100%;
  `,
  content: css`
    padding: 24px 16px 0px 16px;
    flex-grow: 1;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 7px;
  `,
  divider: css`
    padding-top: 15px;
  `
};

